import { Box, Checkbox, Link, Typography } from '@mui/material'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../app/hooks'
import { useGetUnassignedConsignmentsQuery, useLazyGetUnassignedConsignmentLoadsQuery } from '../../app/redux-fetch/apiOrders'
import AddButton from '../../components/Buttons/AddButton'
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2'
import rootEnum from '../../components/Routes/rootEnum'
import ConsignmentLoadsSidePanel from './components/ConsignmentLoadsSidePanel'
import ConsignmentContentsSidePanel from './components/ConsignmentContentsSidePanel'
import ConsignmentServicesSidePanel from './components/ConsignmentServicesSidePanel'
import EmbeddedWaypointsTable from './components/EmbeddedWaypointsTable'
import useJourneyStepperContext from './hooks/useJourneyStepperContext'
import { messages } from './messages'
import { fetchLoggedInUserSelector } from '../selectors'
import { UnassignedConsignment, UnassignedLoad, UnassignedWaypoint } from './types'
import { setSelectedLoads, addLoadMetadata } from './Planner/state/journeyPlannerSlice'

type UnassignedLoadsTableProps = {
  isAddLoadMode?: boolean
}

const UnassignedLoadsTable = ({ isAddLoadMode = false }: UnassignedLoadsTableProps) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const reduxDispatch = useDispatch()

  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data)
  const tenantId = loggedInUser?.tenantId
  const { data: consignments, isSuccess: isConsignmentsLoaded } = useGetUnassignedConsignmentsQuery(
    { tenantId: tenantId as number },
    { skip: !tenantId }
  )

    const { selectedLoads, setSelectedLoads: setContextSelectedLoads } = useJourneyStepperContext()

    const [fetchConsignmentLoads] = useLazyGetUnassignedConsignmentLoadsQuery()

    const totalSelectedLoads = selectedLoads.length

  const getSelectedLoadIds = () => selectedLoads.map((sl) => sl.loadId)

  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const [selectedConsignment, setSelectedConsignment] = useState<UnassignedConsignment | null>(null)

  // State for Contents side panel (opened from Quantity column)
  const [contentsSidePanelOpen, setContentsSidePanelOpen] = useState(false)
  const [contentsConsignment, setContentsConsignment] = useState<UnassignedConsignment | null>(null)

  // State for Services side panel (opened from Services column in nested waypoints table)
  const [servicesSidePanelOpen, setServicesSidePanelOpen] = useState(false)
  const [servicesConsignment, setServicesConsignment] = useState<UnassignedConsignment | null>(null)
  const [servicesWaypoint, setServicesWaypoint] = useState<UnassignedWaypoint | null>(null)

  const handleOpenSidePanel = (consignment: UnassignedConsignment) => {
    setSelectedConsignment(consignment)
    setSidePanelOpen(true)
  }

  const handleCloseSidePanel = () => {
    setSidePanelOpen(false)
    setSelectedConsignment(null)
  }

  const handleOpenContentsSidePanel = (consignment: UnassignedConsignment) => {
    setContentsConsignment(consignment)
    setContentsSidePanelOpen(true)
  }

  const handleCloseContentsSidePanel = () => {
    setContentsSidePanelOpen(false)
    setContentsConsignment(null)
  }

  const handleOpenServicesSidePanel = (consignment: UnassignedConsignment, waypoint: UnassignedWaypoint) => {
    setServicesConsignment(consignment)
    setServicesWaypoint(waypoint)
    setServicesSidePanelOpen(true)
  }

  const handleCloseServicesSidePanel = () => {
    setServicesSidePanelOpen(false)
    setServicesConsignment(null)
    setServicesWaypoint(null)
  }

const getSelectedCountForConsignment = (consignment: UnassignedConsignment): number => {
    return selectedLoads.filter((sl) => sl.shipmentBookingId === consignment.shipmentBookingId).length
  }

  const getTotalLoadsForConsignment = (consignment: UnassignedConsignment): number => {
    return consignment.loads?.length || consignment.unassignedLoadCount
  }

    const handleConsignmentCheckboxChange = async (consignment: UnassignedConsignment, isCurrentlyChecked: boolean) => {
      if (isCurrentlyChecked) {
        // Deselect all loads from this consignment
        setContextSelectedLoads(
          selectedLoads.filter((sl) => sl.shipmentBookingId !== consignment.shipmentBookingId)
        )
      } else {
        // Select all loads from this consignment - need to fetch loads first
        try {
          const result = await fetchConsignmentLoads({
            tenantId: tenantId as number,
            shipmentBookingId: consignment.shipmentBookingId,
          }).unwrap()
          
          // Store load metadata in Redux so tracking numbers are available in Journey Planner
          reduxDispatch(addLoadMetadata(result))
          
          const newLoadsToAdd = result
            .filter((load) => !selectedLoads.some((sl) => sl.loadId === load.loadId))
            .map((load) => ({
              loadId: load.loadId,
              shipmentBookingId: consignment.shipmentBookingId,
            }))
          setContextSelectedLoads([...selectedLoads, ...newLoadsToAdd])
        } catch (error) {
          console.error('Failed to fetch loads for consignment:', error)
        }
      }
    }

  const columns = useMemo<MRTColumnDef<UnassignedConsignment>[]>(() => {
    return [
      {
        accessorFn: (row) => row.trackingNumber ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.trackingNumber),
      },
      {
        accessorFn: (row) => row.loadTypeName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.type),
      },
      {
        accessorFn: (row) => row.unassignedLoadCount,
        header: formatMessage(messages.quantity),
        Cell: ({ row }) => (
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation()
              handleOpenContentsSidePanel(row.original)
            }}
            sx={{ cursor: 'pointer' }}
          >
            {row.original.unassignedLoadCount}
          </Link>
        ),
      },
      {
        accessorFn: (row) => `${row.temperatureControlledCount}/${row.unassignedLoadCount}`,
        header: formatMessage(messages.temperatureControlled),
      },
      {
        accessorFn: (row) => row.shipFromLocationName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.shipFrom),
      },
      {
        accessorFn: (row) => row.waypointCount,
        header: formatMessage(messages.waypoints),
      },
      {
        accessorFn: (row) => row.deliverToLocationName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.deliverTo),
      },
      {
        accessorFn: (row) => row.customerName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.customer),
      },
        {
          id: 'selected',
          accessorFn: (row) => {
            const selected = getSelectedCountForConsignment(row)
            const total = row.unassignedLoadCount
            return formatMessage(messages.selectedOfTotal, { selected, total })
          },
          header: formatMessage(messages.selected),
          Cell: ({ row }) => {
            const selected = getSelectedCountForConsignment(row.original)
            const total = row.original.unassignedLoadCount
            return (
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenSidePanel(row.original)
                }}
                sx={{ cursor: 'pointer' }}
              >
                {formatMessage(messages.selectedOfTotal, { selected, total })}
              </Link>
            )
          },
        },
        {
          id: 'selectAll',
          header: '',
          size: 50,
          Cell: ({ row }) => {
            const consignment = row.original
            const selectedCount = getSelectedCountForConsignment(consignment)
            const totalCount = getTotalLoadsForConsignment(consignment)
            const isChecked = selectedCount > 0 && selectedCount === totalCount
            const isIndeterminate = selectedCount > 0 && selectedCount < totalCount

            return (
              <Checkbox
                checked={isChecked}
                indeterminate={isIndeterminate}
                onChange={(e) => {
                  e.stopPropagation()
                  handleConsignmentCheckboxChange(consignment, isChecked)
                }}
                onClick={(e) => e.stopPropagation()}
              />
            )
          },
        },
      ]
    }, [formatMessage, selectedLoads])

  return (
    <>
      <ConsignmentLoadsSidePanel
        open={sidePanelOpen}
        onClose={handleCloseSidePanel}
        consignment={selectedConsignment}
        tenantId={tenantId as number}
      />
      <ConsignmentContentsSidePanel
        open={contentsSidePanelOpen}
        onClose={handleCloseContentsSidePanel}
        consignment={contentsConsignment}
        tenantId={tenantId as number}
      />
      <ConsignmentServicesSidePanel
        open={servicesSidePanelOpen}
        onClose={handleCloseServicesSidePanel}
        consignment={servicesConsignment}
        tenantId={tenantId as number}
        waypoint={servicesWaypoint}
      />
      <MRTDataGridv2
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          sorting: [
            {
              id: 'trackingNumber',
              desc: false,
            },
          ],
        }}
        state={{ isLoading: !tenantId || !isConsignmentsLoaded }}
        columns={columns}
        data={consignments || []}
        getRowId={(row) => row.shipmentBookingId as any}
        enableSorting={true}
        enableFilters={false}
        enableColumnActions={false}
        enableExpandAll={true}
        enableRowActions={false}
        positionActionsColumn='last'
        renderDetailPanel={({ row }) => {
          const waypoints = row.original.waypoints
          if (!waypoints || waypoints.length === 0) {
            return null
          }
          return (
            <EmbeddedWaypointsTable
              waypoints={waypoints}
              onServicesClick={(waypoint) => handleOpenServicesSidePanel(row.original, waypoint)}
            />
          )
        }}
        {...(!isAddLoadMode
          ? {
              rightHeadingComponent: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatMessage(messages.loadsSelected, { count: totalSelectedLoads })}
                  </Typography>
                  <AddButton
                    text={formatMessage(messages.planJourney)}
                    onClick={() => {
                      const allLoads: UnassignedLoad[] = consignments?.flatMap((c) => c.loads || []) || []
                      const selectedUnassignedLoads = allLoads.filter((load) =>
                        selectedLoads.some((sl) => sl.loadId === load.loadId)
                      )
                      // Get consignments that have selected loads
                      const selectedConsignments = consignments?.filter((c) =>
                        selectedLoads.some((sl) => sl.shipmentBookingId === c.shipmentBookingId)
                      ) || []
                      reduxDispatch(
                        setSelectedLoads({
                          loads: selectedLoads,
                          unassignedLoads: selectedUnassignedLoads,
                          consignments: selectedConsignments,
                        })
                      )
                      navigate(rootEnum.JOURNEY_PLANNER)
                    }}
                    disabled={totalSelectedLoads === 0}
                  />
                </Box>
              ),
            }
          : {})}
      />
    </>
  )
}

export default UnassignedLoadsTable
